import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Check } from "lucide-react"

export const CONTEXT_MENU_Z_INDEX = 2000
export const CONTEXT_MENU_VIEWPORT_PADDING_PX = 12
export const CONTEXT_MENU_ANCHOR_GAP_PX = 4
export const CONTEXT_MENU_MIN_WIDTH_PX = 196
export const CONTEXT_MENU_ITEM_FEEDBACK_MS = 150

/** @type {React.Context<{ pendingSelection: { itemKey: string, active: boolean, exclusiveGroup?: string } | null, selectItem: (itemKey: string, nextActive: boolean, onClick: () => void, exclusiveGroup?: string) => void } | null>} */
const ContextMenuInteractionContext = React.createContext(null)

const CONTEXT_MENU_STYLE_ID = "v2-context-menu-styles"

const CONTEXT_MENU_CSS = `
.v2-context-menu.dropdown-menu {
    --bs-dropdown-min-width: 12.25rem;
    --bs-dropdown-padding-x: 0;
    --bs-dropdown-padding-y: 0.375rem;
    --bs-dropdown-link-active-bg: rgba(15, 97, 112, 0.12);
    --bs-dropdown-link-active-color: #0f6170;
    --bs-dropdown-link-hover-bg: rgba(15, 97, 112, 0.08);
    --bs-dropdown-link-hover-color: #212529;
    border-color: rgba(0, 0, 0, 0.1);
    box-shadow: 0 0.35rem 1rem rgba(15, 23, 42, 0.12);
    font-size: 0.875rem;
}

.v2-context-menu .dropdown-item {
    padding: 0.45rem 0.875rem;
    font-size: 0.875rem;
    line-height: 1.35;
}

.v2-context-menu .dropdown-item.active,
.v2-context-menu .dropdown-item:active {
    font-weight: 600;
}

.v2-context-menu .dropdown-divider {
    margin: 0.25rem 0;
    opacity: 1;
    border-top-color: rgba(0, 0, 0, 0.08);
}

.v2-context-menu .v2-context-menu-item-trailing {
    width: 16px;
    flex-shrink: 0;
}
`

function ensureContextMenuStyles() {
    if (typeof document === "undefined") {
        return
    }
    if (document.getElementById(CONTEXT_MENU_STYLE_ID)) {
        return
    }
    const styleEl = document.createElement("style")
    styleEl.id = CONTEXT_MENU_STYLE_ID
    styleEl.textContent = CONTEXT_MENU_CSS
    document.head.appendChild(styleEl)
}

ensureContextMenuStyles()

/**
 * @param {DOMRect} anchorRect
 * @param {HTMLElement | null | undefined} menuEl
 * @param {number} [minWidth]
 * @returns {{ top: number, left: number, minWidth: number }}
 */
export function computeContextMenuPosition(
    anchorRect,
    menuEl,
    minWidth = CONTEXT_MENU_MIN_WIDTH_PX
) {
    const resolvedMinWidth = Math.max(anchorRect.width, minWidth)
    const menuWidth = menuEl ?
        Math.max(menuEl.offsetWidth, resolvedMinWidth) :
        resolvedMinWidth
    const menuHeight = menuEl?.offsetHeight ?? 0
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let left = anchorRect.left
    let top = anchorRect.bottom + CONTEXT_MENU_ANCHOR_GAP_PX

    if (left + menuWidth > viewportWidth - CONTEXT_MENU_VIEWPORT_PADDING_PX) {
        left = anchorRect.right - menuWidth
    }
    left = Math.max(
        CONTEXT_MENU_VIEWPORT_PADDING_PX,
        Math.min(left, viewportWidth - CONTEXT_MENU_VIEWPORT_PADDING_PX - menuWidth)
    )

    if (menuHeight > 0) {
        if (top + menuHeight > viewportHeight - CONTEXT_MENU_VIEWPORT_PADDING_PX) {
            const topAbove = anchorRect.top - menuHeight - CONTEXT_MENU_ANCHOR_GAP_PX
            if (topAbove >= CONTEXT_MENU_VIEWPORT_PADDING_PX) {
                top = topAbove
            } else {
                top = Math.max(
                    CONTEXT_MENU_VIEWPORT_PADDING_PX,
                    viewportHeight - CONTEXT_MENU_VIEWPORT_PADDING_PX - menuHeight
                )
            }
        }
        top = Math.max(
            CONTEXT_MENU_VIEWPORT_PADDING_PX,
            Math.min(top, viewportHeight - CONTEXT_MENU_VIEWPORT_PADDING_PX - menuHeight)
        )
    }

    return { top, left, minWidth: resolvedMinWidth }
}

/**
 * @param {{
 *   active?: boolean,
 *   disabled?: boolean,
 *   onClick?: () => void,
 *   children: React.ReactNode,
 *   showActiveCheck?: boolean,
 *   trailing?: React.ReactNode,
 *   className?: string,
 *   itemKey?: string,
 *   activeFeedback?: "select" | "toggle",
 *   exclusiveGroup?: string,
 *   clearOnReselect?: boolean,
 * }} props
 */
export function ContextMenuItem({
    active = false,
    disabled = false,
    onClick = () => {},
    children,
    showActiveCheck = true,
    trailing = null,
    className = "",
    itemKey = "",
    activeFeedback = "select",
    exclusiveGroup = "",
    clearOnReselect = false,
}) {
    const interaction = React.useContext(ContextMenuInteractionContext)
    const [localPendingActive, setLocalPendingActive] = useState(/** @type {boolean | null} */(null))

    let displayActive = active
    if (interaction?.pendingSelection && itemKey) {
        const pending = interaction.pendingSelection
        if (pending.itemKey === itemKey) {
            displayActive = pending.active
        } else if (
            exclusiveGroup &&
            pending.exclusiveGroup &&
            exclusiveGroup === pending.exclusiveGroup
        ) {
            displayActive = false
        } else if (!exclusiveGroup && !pending.exclusiveGroup && activeFeedback === "select") {
            displayActive = false
        }
    } else if (localPendingActive != null) {
        displayActive = localPendingActive
    }

    function handleClick() {
        if (disabled) {
            return
        }

        const nextActive = activeFeedback === "toggle" ?
            !active :
            (clearOnReselect && active ? false : true)

        if (interaction && itemKey) {
            interaction.selectItem(itemKey, nextActive, onClick, exclusiveGroup || undefined)
            return
        }

        setLocalPendingActive(nextActive)
        window.setTimeout(() => {
            onClick()
            setLocalPendingActive(null)
        }, CONTEXT_MENU_ITEM_FEEDBACK_MS)
    }

    return (
        <button
            type="button"
            role="menuitem"
            disabled={disabled}
            className={
                "dropdown-item d-flex align-items-center justify-content-between gap-3 " +
                (displayActive ? "active " : "") +
                className
            }
            onClick={handleClick}
        >
            <span>{children}</span>
            {trailing ?? (
                showActiveCheck ? (
                    displayActive ? (
                        <Check size={16} aria-hidden className="v2-context-menu-item-trailing" />
                    ) : (
                        <span className="v2-context-menu-item-trailing" aria-hidden />
                    )
                ) : null
            )}
        </button>
    )
}

export function ContextMenuDivider() {
    return <div className="dropdown-divider my-1" />
}

/**
 * Portaled Bootstrap-styled context menu anchored to a trigger element.
 *
 * @param {{
 *   open: boolean,
 *   onOpenChange: (nextOpen: boolean) => void,
 *   anchorRef: React.RefObject<HTMLElement | null>,
 *   children: React.ReactNode,
 *   minWidth?: number,
 *   zIndex?: number,
 *   className?: string,
 *   repositionDeps?: React.DependencyList,
 *   itemCloseDelayMs?: number,
 * }} props
 */
export default function ContextMenu({
    open,
    onOpenChange,
    anchorRef,
    children,
    minWidth = CONTEXT_MENU_MIN_WIDTH_PX,
    zIndex = CONTEXT_MENU_Z_INDEX,
    className = "",
    repositionDeps = [],
    itemCloseDelayMs = CONTEXT_MENU_ITEM_FEEDBACK_MS,
}) {
    const menuRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const closeTimerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */(null))
    const [menuPosition, setMenuPosition] = useState(
        /** @type {{ top: number, left: number, minWidth: number } | null} */(null)
    )
    const [pendingSelection, setPendingSelection] = useState(
        /** @type {{ itemKey: string, active: boolean, exclusiveGroup?: string } | null} */(null)
    )

    const selectItem = useCallback((itemKey, nextActive, onClick, exclusiveGroup) => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current)
        }
        setPendingSelection({
            itemKey,
            active: nextActive,
            ...(exclusiveGroup ? { exclusiveGroup } : {}),
        })
        closeTimerRef.current = setTimeout(() => {
            onClick()
            onOpenChange(false)
            setPendingSelection(null)
            closeTimerRef.current = null
        }, itemCloseDelayMs)
    }, [itemCloseDelayMs, onOpenChange])

    useEffect(() => {
        if (open) {
            return
        }
        setPendingSelection(null)
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current)
            closeTimerRef.current = null
        }
    }, [open])

    useEffect(() => () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current)
        }
    }, [])

    useLayoutEffect(() => {
        if (!open || !anchorRef.current) {
            setMenuPosition(null)
            return
        }

        function syncMenuPosition() {
            const anchor = anchorRef.current
            if (!anchor) {
                return
            }
            const rect = anchor.getBoundingClientRect()
            setMenuPosition(computeContextMenuPosition(rect, menuRef.current, minWidth))
        }

        syncMenuPosition()
        const rafId = window.requestAnimationFrame(syncMenuPosition)
        window.addEventListener("resize", syncMenuPosition)
        window.addEventListener("scroll", syncMenuPosition, true)
        return () => {
            window.cancelAnimationFrame(rafId)
            window.removeEventListener("resize", syncMenuPosition)
            window.removeEventListener("scroll", syncMenuPosition, true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, anchorRef, minWidth, ...repositionDeps])

    useEffect(() => {
        if (!open) {
            return
        }
        function handlePointerDown(event) {
            const target = event.target
            if (!(target instanceof Node)) {
                return
            }
            if (anchorRef.current?.contains(target)) {
                return
            }
            if (menuRef.current?.contains(target)) {
                return
            }
            onOpenChange(false)
        }
        function handleKeyDown(event) {
            if (event.key === "Escape") {
                onOpenChange(false)
            }
        }
        document.addEventListener("mousedown", handlePointerDown)
        document.addEventListener("keydown", handleKeyDown)
        return () => {
            document.removeEventListener("mousedown", handlePointerDown)
            document.removeEventListener("keydown", handleKeyDown)
        }
    }, [open, onOpenChange, anchorRef])

    if (!open || !menuPosition) {
        return null
    }

    return createPortal(
        <ContextMenuInteractionContext.Provider value={{ pendingSelection, selectItem }}>
            <div
                ref={menuRef}
                role="menu"
                className={`dropdown-menu show v2-context-menu ${className}`.trim()}
                style={{
                    position: "fixed",
                    display: "block",
                    top: `${menuPosition.top}px`,
                    left: `${menuPosition.left}px`,
                    zIndex,
                    minWidth: `${menuPosition.minWidth}px`,
                    maxWidth: `calc(100vw - ${CONTEXT_MENU_VIEWPORT_PADDING_PX * 2}px)`,
                    margin: 0,
                }}
            >
                {children}
            </div>
        </ContextMenuInteractionContext.Provider>,
        document.body
    )
}
